/* Offer bundle — <product-offer-bundle> "1+1" / "2+2" multi-brush cards.
   Brush 1 is the main line (main variant picker); brushes 2-4 are extra lines
   whose colours come from the pickers the variant-picker block renders with
   `offer_extra_brushes` (wrappers [data-offer-extra-brush="N"]). */
if (!customElements.get('product-offer-bundle')) {
  class ProductOfferBundle extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      this.section = this.closest('.js-product') || document;
      this.radios = Array.from(this.querySelectorAll('.product-offer__radio'));
      this.priceEls = Array.from(this.querySelectorAll('.js-price'));
      this.summary = this.querySelector('[data-offer-summary]');
      this.bundleInputs = Array.from(this.querySelectorAll('[data-offer-bundle-id]'));

      const formId = this.dataset.formId || '';
      this.mainPicker = this.section.querySelector(`variant-picker[data-form-id="${formId}"]:not([data-simple-mode])`);
      this.extraWrappers = Array.from(this.section.querySelectorAll('[data-offer-extra-brush]'));

      this.update = this.update.bind(this);
      this.radios.forEach((radio) => radio.addEventListener('change', this.update));
      if (this.mainPicker) this.mainPicker.addEventListener('change', this.update);
      this.extraWrappers.forEach((wrapper) => wrapper.addEventListener('change', this.update));
      // Purchase type (subscription/one-time) flips re-render the prices.
      window.addEventListener('on:price:changed', this.update);

      // The pickers sit before this script runs (it's deferred), so sync now; once
      // more on the next frame in case a picker restores its selection late.
      this.update();
      requestAnimationFrame(this.update);
    }

    get selectedBrushes() {
      const checked = this.radios.find((radio) => radio.checked) || this.radios[0];
      return checked ? +checked.dataset.brushes : 1;
    }

    get isOnetime() {
      return (window.purchaseOptions?.purchase_type || 'onetime') === 'onetime';
    }

    /**
     * Selected variant of a variant-picker, read from its embedded JSON and checked
     * options (same approach as <product-offer>, independent of picker state timing).
     * @param {?Element} pickerEl - The <variant-picker> element.
     * @returns {?object} The matching variant, or null.
     */
    getPickerVariant(pickerEl) {
      if (!pickerEl) return null;

      let variants = pickerEl._offerVariants;
      if (!variants) {
        const dataEl = pickerEl.querySelector('script[type="application/json"]');
        if (!dataEl) return null;
        try {
          variants = JSON.parse(dataEl.textContent).variants;
        } catch (e) {
          return null;
        }
        pickerEl._offerVariants = variants;
      }

      const selected = [];
      pickerEl.querySelectorAll('.option-selector').forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          const opt = selector.querySelector('.custom-select__option[aria-selected="true"]');
          selected.push(opt && opt.dataset.value !== '' ? opt.dataset.value : null);
        } else {
          const opt = selector.querySelector('input:checked');
          selected.push(opt ? opt.value : null);
        }
      });

      return variants.find((v) => v.options.every((val, i) => val === selected[i])) || null;
    }

    /** Variant per brush position (1-based); unpicked brushes follow brush 1. */
    getBrushVariants() {
      const main = this.getPickerVariant(this.mainPicker);
      const variants = { 1: main };
      this.extraWrappers.forEach((wrapper) => {
        const picker = wrapper.querySelector('variant-picker');
        variants[+wrapper.dataset.offerExtraBrush] = this.getPickerVariant(picker) || main;
      });
      return variants;
    }

    /**
     * Mirrors `percentForPosition` in the volume-discount function: the tier with
     * the highest minimum quantity at or below `position` wins.
     */
    percentForPosition(tiers, position) {
      let best = 0;
      let percent = 0;
      (Array.isArray(tiers) ? tiers : []).forEach((tier) => {
        const [rawMin, rawPercent] = String(tier).split('--');
        const min = parseFloat(String(rawMin).replace('+', ''));
        const pct = parseFloat(String(rawPercent).replace('+', ''));
        if (!Number.isFinite(min) || !Number.isFinite(pct) || min <= 0) return;
        if (min <= position && min >= best) {
          best = min;
          percent = pct;
        }
      });
      return percent;
    }

    /** Total for a set of brush prices (cents), ranked most expensive first like the cart. */
    totalForTiers(tiers, prices) {
      return [...prices]
        .sort((a, b) => b - a)
        .reduce((total, price, i) => total + price - Math.round(price * this.percentForPosition(tiers, i + 1) / 100), 0);
    }

    update() {
      const selected = this.selectedBrushes;
      const variants = this.getBrushVariants();

      // Extra line ids/quantities and picker visibility.
      this.querySelectorAll('[data-offer-brush-id]').forEach((input) => {
        const brush = +input.dataset.offerBrushId;
        if (variants[brush]) input.value = variants[brush].id;
      });
      this.querySelectorAll('[data-offer-brush-qty]').forEach((input) => {
        input.value = +input.dataset.offerBrushQty <= selected ? '1' : '0';
      });
      this.extraWrappers.forEach((wrapper) => {
        wrapper.hidden = +wrapper.dataset.offerExtraBrush > selected;
      });
      // Bundle ids only travel with a subscription purchase (see subscription-form).
      this.bundleInputs.forEach((input) => { input.disabled = this.isOnetime; });

      if (variants[1]) this.recalcPrices(variants);
      this.renderPrices();
    }

    recalcPrices(variants) {
      const po = window.purchaseOptions || {};
      const onetimeTiers = po.onetime_prices;
      const subsTiers = Array.isArray(po.subscription_prices) && po.subscription_prices.length
        ? po.subscription_prices
        : onetimeTiers;

      this.priceEls.forEach((priceEl) => {
        const count = +priceEl.dataset.brushes || 1;
        const prices = [];
        for (let brush = 1; brush <= count; brush++) prices.push((variants[brush] || variants[1]).price);

        priceEl.dataset.onetimePrice = this.totalForTiers(onetimeTiers, prices);
        priceEl.dataset.subscriptionPrice = this.totalForTiers(subsTiers, prices);
        priceEl.dataset.compareAtPrice = prices.reduce((a, b) => a + b, 0);
      });
    }

    renderPrices() {
      const formatMoney = window.Shopify?.formatMoney;
      if (!formatMoney) return;

      this.priceEls.forEach((priceEl) => {
        const current = this.isOnetime ? +priceEl.dataset.onetimePrice : +priceEl.dataset.subscriptionPrice;
        const compare = +priceEl.dataset.compareAtPrice;

        const currentEl = priceEl.querySelector('.price__current');
        const wasEl = priceEl.querySelector('.price__was');
        const priceRoot = priceEl.querySelector('.price');

        if (currentEl) currentEl.innerHTML = formatMoney(current);
        if (wasEl) {
          wasEl.innerHTML = compare > current ? formatMoney(compare) : '';
          wasEl.style.display = compare > current ? '' : 'none';
        }
        if (priceRoot) priceRoot.classList.toggle('price--on-sale', compare > current);
      });

      this.updateSummary();
    }

    /** Total, struck compare price, badge, savings line and per-brush line for the selected card. */
    updateSummary() {
      if (!this.summary) return;
      const formatMoney = window.Shopify?.formatMoney;
      if (!formatMoney) return;

      const brushes = this.selectedBrushes;
      const priceEl = this.priceEls.find((el) => +el.dataset.brushes === brushes);
      if (!priceEl) return;

      const current = this.isOnetime ? +priceEl.dataset.onetimePrice : +priceEl.dataset.subscriptionPrice;
      const compare = +priceEl.dataset.compareAtPrice;
      if (!compare) return;

      const hasDiscount = compare > current;
      const currentEl = this.summary.querySelector('[data-summary-current]');
      const wasEl = this.summary.querySelector('[data-summary-was]');
      const badgeEl = this.summary.querySelector('[data-summary-badge]');
      const saveEl = this.summary.querySelector('[data-summary-save]');
      const perBrushEl = this.summary.querySelector('[data-summary-perbrush]');

      if (currentEl) currentEl.innerHTML = formatMoney(current);
      if (wasEl) {
        wasEl.innerHTML = hasDiscount ? formatMoney(compare) : '';
        wasEl.hidden = !hasDiscount;
      }
      if (badgeEl) {
        badgeEl.textContent = priceEl.dataset.badge || '';
        badgeEl.hidden = !priceEl.dataset.badge;
      }
      if (saveEl) {
        if (hasDiscount) {
          const prefix = this.summary.dataset.savePrefix || '';
          const note = this.summary.dataset.note || '';
          saveEl.innerHTML = [prefix, formatMoney(compare - current)].filter(Boolean).join(' ') + (note ? ` — ${note}` : '');
          saveEl.hidden = false;
        } else {
          saveEl.hidden = true;
        }
      }
      if (perBrushEl) {
        const tpl = this.summary.dataset.perBrush || '';
        perBrushEl.innerHTML = tpl ? tpl.replace('[price]', formatMoney(Math.round(current / brushes))) : '';
        perBrushEl.hidden = !tpl;
      }

      this.summary.hidden = false;
    }
  }

  customElements.define('product-offer-bundle', ProductOfferBundle);
}
